/**
 * Smart-default generator — phase 27 Slice B.
 *
 * Replaced the per-action hand-maintained `EXAMPLE_INPUTS` map the old
 * `/actions` page carried (that page was retired 2026-08-04; the renderer
 * lives on in `<DispatchForm>`, hosted by `/status`). The renderer asks
 * `generateSmartDefault(node, fieldName)` for each leaf field and gets a
 * sensible starter value back.
 *
 * Heuristics (in priority order):
 *
 *   - Enum: first allowed value.
 *   - Integer with `minimum`: `minimum`. Else 0.
 *   - Boolean: `false`.
 *   - Text with `format: 'email'`: `'test@example.com'`.
 *   - Text with `format: 'uuid'`: a stable UUID-v7-shaped placeholder.
 *   - Text with `format: 'phone'` OR pattern matching E.164: `'+447700900000'`.
 *   - Text whose field name ends with `_id`: UUID v7 placeholder.
 *   - Text whose pattern looks like a kebab-slug pattern: `'example-slug'`.
 *   - Text otherwise: empty string.
 *
 * Pure: no IO, no DOM, no random. Deterministic for a given (node,
 * fieldName) pair. Tests assert each branch.
 */

import type { FormTreeNode } from "./compiler.js";

/**
 * UUID v7 placeholder. Stable shape that compiles against the UUID_V7
 * regex used in every action's input schema:
 *
 *   ^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$
 */
export const UUID_V7_PLACEHOLDER = "01234567-89ab-7def-8123-456789abcdef";

/**
 * E.164 phone placeholder — UK mobile format with the +44 7700 900XXX
 * reserved-for-fiction range. Compiles against Layer's E.164 pattern
 * `^\+[1-9]\d{1,14}$`.
 */
export const E164_PHONE_PLACEHOLDER = "+447700900000";

/** Reserved-for-fiction email placeholder. */
export const EMAIL_PLACEHOLDER = "test@example.com";

/** Kebab-slug placeholder — compiles against `^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$`. */
export const SLUG_PLACEHOLDER = "example-slug";

/**
 * Generate a sensible starter value for a single leaf node. The form
 * renderer calls this per-field at mount time; the user can edit
 * away. `fieldName` is the parent object's property key (used for
 * heuristics — `*_id` → UUID).
 */
export function generateSmartDefault(
  node: FormTreeNode,
  fieldName: string,
): unknown {
  switch (node.kind) {
    case "enum":
      return node.values[0] ?? "";
    case "integer":
      if (typeof node.minimum === "number") return node.minimum;
      return 0;
    case "boolean":
      return false;
    case "text":
      // Lookup fields default blank so the dropdown shows its
      // "Choose…" placeholder instead of a synthetic UUID/slug value
      // that doesn't exist in the database.
      if (node.lookupEntity !== undefined) return "";
      // Format hint takes priority.
      if (node.format === "email") return EMAIL_PLACEHOLDER;
      if (node.format === "uuid") return UUID_V7_PLACEHOLDER;
      if (node.format === "phone") return E164_PHONE_PLACEHOLDER;
      // Datetime stays blank — the picker shows a placeholder and the
      // user MUST choose a real value (no sensible default — "now" would
      // be wrong for slot scheduling, occurred_at backfills, etc).
      if (node.format === "datetime") return "";
      // Field-name heuristics.
      if (/_id$/.test(fieldName)) return UUID_V7_PLACEHOLDER;
      if (/email/i.test(fieldName)) return EMAIL_PLACEHOLDER;
      if (/phone/i.test(fieldName)) return E164_PHONE_PLACEHOLDER;
      // Pattern-shape heuristics.
      if (
        typeof node.pattern === "string" &&
        node.pattern.includes("[a-z0-9]") &&
        node.pattern.includes("[a-z0-9-]")
      ) {
        return SLUG_PLACEHOLDER;
      }
      // Optional with no constraints — return empty so the form
      // doesn't ship junk data on submit.
      return "";
    case "array":
      return [];
    case "object": {
      // Build a default object by recursing into each REQUIRED field.
      // Optional fields stay omitted so the action's input schema's
      // `additionalProperties: false` doesn't reject the form on
      // submit. The user adds optional fields via the renderer.
      const out: Record<string, unknown> = {};
      for (const fname of node.required) {
        const child = node.properties[fname];
        if (child !== undefined) {
          out[fname] = generateSmartDefault(child, fname);
        }
      }
      return out;
    }
    case "oneOf":
      // Choose the first variant by default.
      if (node.variants.length === 0) return {};
      return generateSmartDefault(node.variants[0]!.node, fieldName);
  }
}
